import {
  ChangeDetectionStrategy, Component, EventEmitter, Output, signal, inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { AdminForbiddenError } from '../../services/admin.service.js';
import { AdminDashboardComponent } from './admin-dashboard.component.js';
import { AdminUsersComponent } from './admin-users.component.js';
import { AdminAuditLogComponent } from './admin-audit-log.component.js';
import { AdminToolsComponent } from './admin-tools.component.js';

type Tab = 'dashboard' | 'users' | 'audit' | 'tools';

// Shared error sink: child components route AdminForbiddenError up here so the
// panel can close itself + alert. Other errors stay in the child component.
@Component({
  selector: 'app-admin-panel',
  standalone: true,
  imports: [CommonModule, AdminDashboardComponent, AdminUsersComponent, AdminAuditLogComponent, AdminToolsComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(document:keydown.escape)': 'close.emit()',
  },
  template: `
    <div class="fixed inset-0 z-[100] bg-gray-950/95 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in"
         (click)="close.emit()">
      <div class="bg-gray-900 border border-gray-800 rounded-xl shadow-2xl w-full max-w-7xl max-h-[92vh] flex flex-col"
           (click)="$event.stopPropagation()">

        <!-- Header -->
        <header class="flex items-center justify-between p-4 border-b border-gray-800 shrink-0">
          <div class="flex items-center gap-2.5">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
            </svg>
            <h2 class="text-lg font-bold text-purple-300">Админ-панель</h2>
          </div>

          <!-- Tabs -->
          <nav class="flex gap-1 bg-gray-950 rounded-lg p-1 border border-gray-800">
            <button (click)="tab.set('dashboard')" [class]="tabClass('dashboard')" class="px-3 py-1.5 text-xs font-medium rounded transition-colors">Дашборд</button>
            <button (click)="tab.set('users')" [class]="tabClass('users')" class="px-3 py-1.5 text-xs font-medium rounded transition-colors">Пользователи</button>
            <button (click)="tab.set('audit')" [class]="tabClass('audit')" class="px-3 py-1.5 text-xs font-medium rounded transition-colors">Аудит</button>
            <button (click)="tab.set('tools')" [class]="tabClass('tools')" class="px-3 py-1.5 text-xs font-medium rounded transition-colors">Инструменты</button>
          </nav>

          <button (click)="close.emit()"
                  class="p-1.5 rounded text-gray-400 hover:text-white hover:bg-gray-800 transition-colors"
                  title="Закрыть (Esc)">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </header>

        <!-- Body -->
        <div class="flex-1 overflow-y-auto">
          @if (forbiddenAlert()) {
            <div class="m-4 p-3 bg-red-950/40 border border-red-900/60 rounded-lg text-sm text-red-300">
              Права администратора были отозваны. Закройте панель.
            </div>
          } @else {
            @switch (tab()) {
              @case ('dashboard') { <app-admin-dashboard (forbidden)="onForbidden()" /> }
              @case ('users')     { <app-admin-users     (forbidden)="onForbidden()" /> }
              @case ('audit')     { <app-admin-audit-log (forbidden)="onForbidden()" /> }
              @case ('tools')     { <app-admin-tools     (forbidden)="onForbidden()" /> }
            }
          }
        </div>
      </div>
    </div>
  `,
})
export class AdminPanelComponent {
  @Output() close = new EventEmitter<void>();

  tab = signal<Tab>('dashboard');
  forbiddenAlert = signal(false);

  tabClass(t: Tab): string {
    return this.tab() === t
      ? 'bg-purple-600/30 text-purple-200 border border-purple-500/50'
      : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800/60 border border-transparent';
  }

  onForbidden() {
    this.forbiddenAlert.set(true);
  }
}
